import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MessageCircle, Globe2, Leaf, PackageCheck } from "lucide-react";
import { scrollToId, trackEvent, waLink } from "@/lib/vgt";

const reveal = {
  hidden: { y: "115%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.2 + i * 0.16, ease: [0.22, 1, 0.36, 1] },
  }),
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const yCard = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="relative overflow-hidden pt-28 sm:pt-36 pb-16 sm:pb-24 px-4 sm:px-8 bg-gradient-to-b from-[#FDFBF7] via-[#F8F3EA] to-[#FDFBF7]"
    >
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.05]"
        style={{ backgroundImage: "radial-gradient(#2C1A0C 1px, transparent 1px)", backgroundSize: "24px 24px" }}
      />
      <div className="relative max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 lg:gap-8 items-center">
        <div className="lg:col-span-7">
          <motion.p
            data-testid="hero-eyebrow"
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="font-mono text-xs sm:text-sm tracking-[0.3em] uppercase text-[#5C3A21] mb-6"
          >
            Vanilla Global Transindo · Indonesia
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="font-serif font-bold tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl text-[#2C1A0C]"
          >
            {["Premium Indonesian Vanilla", "for Global B2B Buyers"].map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span className="block" custom={i} variants={reveal} initial="hidden" animate="show">
                  {i === 0 ? (
                    line
                  ) : (
                    <>
                      for Global <span className="italic text-[#2E4F3E]">B2B</span> Buyers
                    </>
                  )}
                </motion.span>
              </span>
            ))}
          </h1>
          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.6 }}
            className="mt-6 text-base sm:text-lg text-[#6E5B4F] leading-relaxed max-w-xl"
          >
            Direct-export Planifolia &amp; Tahitensis beans and pure vanilla powder for food manufacturers,
            bakeries, gelato and chocolate makers — with flexible MOQ from 1 kg and worldwide shipping support.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.75 }}
            className="mt-8 flex flex-col sm:flex-row gap-3.5"
          >
            <button
              data-testid="hero-cta-quote-button"
              onClick={() => scrollToId("quotation")}
              className="group inline-flex items-center justify-center gap-2.5 bg-[#2E4F3E] hover:bg-[#1B3B2B] text-[#FDFBF7] font-semibold rounded-full px-8 py-4 text-base transition-colors duration-300"
            >
              Request a Quotation
              <ArrowRight size={17} className="transition-transform duration-300 group-hover:translate-x-1" />
            </button>
            <a
              data-testid="hero-cta-whatsapp-button"
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackEvent("whatsapp_click", { location: "hero" })}
              className="inline-flex items-center justify-center gap-2.5 bg-[#25D366] hover:bg-[#1EAE52] text-white font-semibold rounded-full px-8 py-4 text-base transition-colors duration-300"
            >
              <MessageCircle size={17} />
              Chat on WhatsApp
            </a>
          </motion.div>
          <motion.div
            data-testid="hero-trust-line"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.95 }}
            className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-[#5C3A21]"
          >
            <span className="inline-flex items-center gap-2">
              <Leaf size={15} className="text-[#2E4F3E]" /> Indonesian Supplier
            </span>
            <span className="inline-flex items-center gap-2">
              <PackageCheck size={15} className="text-[#2E4F3E]" /> Export Ready
            </span>
            <span className="inline-flex items-center gap-2">
              <Globe2 size={15} className="text-[#2E4F3E]" /> Worldwide Delivery
            </span>
          </motion.div>
        </div>
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <motion.div style={{ y: yImg }} className="relative overflow-hidden rounded-[2rem] shadow-2xl">
              <img
                data-testid="hero-product-image"
                src="/images/hero-curing-racks.jpg"
                alt="Vanilla Global Transindo — Indonesian vanilla beans on curing racks"
                className="w-full aspect-[16/9] object-cover object-center"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#1C120C]/50 via-transparent to-transparent" />
            </motion.div>
            <motion.div
              data-testid="hero-spec-card"
              style={{ y: yCard }}
              className="absolute -bottom-6 right-4 sm:-right-4 bg-[#FDFBF7]/80 backdrop-blur-xl border border-[#E6DEC3] rounded-2xl shadow-xl px-5 py-4"
            >
              <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-[#5C3A21] mb-1.5">
                Planifolia Grade A
              </p>
              <p className="font-serif font-bold text-[#2C1A0C] text-lg leading-tight">
                Vanillin 2.0–2.4%
              </p>
              <p className="text-xs text-[#6E5B4F] mt-0.5">Moisture 30–35% · 17–22 cm</p>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
